import * as R from 'ramda'
import * as RA from 'ramda-adjunct'
import type {TFormError} from '@kapitalist/contract/types'
import {areStrings} from '@kapitalist/utils/strings'
import type {TGenericizedPathWithHTTPVerb} from '~/api/client/types'

export type TAPIErrorKind =
  | 'formError'
  | 'invalidArguments'
  | 'invalidCSRF'
  | 'loggedOut'
  | 'unknown'

export interface IAPIErrorDataBase {
  kind: TAPIErrorKind
  statusCode: number
  statusText: string
  rawResponseData: unknown

  /**
   * Metadata about the backend request path that we hit (w/o any UUIDs/dynamic things in it),
   * for debugging and error grouping purposes.
   */
  genericizedPathWithHTTPVerb: TGenericizedPathWithHTTPVerb | undefined
}

export interface IAPIErrorDataFormError extends IAPIErrorDataBase {
  kind: 'formError'

  // key: fieldName of the form field, or just another label if not applicable
  // value: list of error messages. guaranteed to have at least one message by isFormError
  formErrors: TFormError['errors']
}

export interface IAPIErrorDataInvalidArguments extends IAPIErrorDataBase {
  kind: 'invalidArguments'
  invalidArgumentsError: TInvalidArgumentsError
}

export interface IAPIErrorDataInvalidCSRF extends IAPIErrorDataBase {
  kind: 'invalidCSRF'
}

export interface IAPIErrorDataLoggedOut extends IAPIErrorDataBase {
  kind: 'loggedOut'
}

export interface IAPIErrorDataUnknown extends IAPIErrorDataBase {
  kind: 'unknown'
  contextualMessage: string
}

export type TAPIErrorData =
  | IAPIErrorDataFormError
  | IAPIErrorDataInvalidArguments
  | IAPIErrorDataInvalidCSRF
  | IAPIErrorDataLoggedOut
  | IAPIErrorDataUnknown

const isRecord = (v: unknown): v is Record<string, unknown> => RA.isPlainObj(v)

/* Confirms e is of the expected shape of a `FormError` from the backend */
export const isFormError = (e: unknown): e is TFormError => {
  if (isRecord(e)) {
    if ('errors' in e && isRecord(e['errors'])) {
      const errors = e['errors']
      const keys = Object.keys(errors)
      return (
        keys.length > 0 &&
        R.all((key: string) => {
          const innerErrors = errors[key]
          return (
            typeof key === 'string' &&
            innerErrors instanceof Array &&
            innerErrors.length > 0 &&
            areStrings(innerErrors)
          )
        }, keys)
      )
    }
  }
  return false
}

/* A type of error the backend sometimes returns, usually for custom-formatted strings that may fail to parse.
 *  For example: {
 *     "message": "Invalid Arguments",
 *     "errors": ["Model/CustomTypes.hs: Error parsing '123–12–3123' as a
 *               Social Security Number. Error: unexpected '–'\nexpecting digit\n"]
 *  }
 */
export type TInvalidArgumentsError = {
  message: 'Invalid Arguments'
  errors: string[]
}

export const isInvalidArgumentsError = (e: any): e is TInvalidArgumentsError => {
  return (
    !!e &&
    e.message === 'Invalid Arguments' &&
    Array.isArray(e.errors) &&
    areStrings(e.errors)
  )
}
